import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { DialogQueryState } from "@/components/shared/DialogQueryState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGet } from "@/lib/api-client";
import { policyKeys } from "@/lib/query";
import { employerContactsSchema } from "@/schemas/policy-dialogs";

interface EmployerContactDialogProps {
  policyNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ContactField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-[11px] font-medium">{label}</div>
      <div className="mt-0.5 break-words font-medium text-sm" title={value ?? undefined}>
        {value ?? "—"}
      </div>
    </div>
  );
}

export function EmployerContactDialog({
  policyNumber,
  open,
  onOpenChange,
}: EmployerContactDialogProps) {
  const {
    data: contacts,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: policyKeys.contacts(policyNumber),
    queryFn: () =>
      apiGet(`/api/policies/${encodeURIComponent(policyNumber)}/contacts`, employerContactsSchema),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Employer Contact</DialogTitle>
          <DialogDescription>Policy {policyNumber}</DialogDescription>
        </DialogHeader>

        <DialogQueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={() => refetch()}
          errorMessage="Failed to load contacts"
          isEmpty={!isLoading && !isError && contacts !== undefined && contacts.length === 0}
          emptyIcon={Users}
          emptyMessage="No employer contacts on file."
          skeletonHeight="h-20"
        />

        {contacts && contacts.length > 0 && (
          <div className="space-y-3">
            {contacts.map((c) => (
              <div key={c.id} className="space-y-4 rounded-lg border p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <ContactField label="Name" value={c.fullName} />
                  <ContactField label="Title" value={c.jobTitle} />
                </div>
                <div>
                  <div className="text-muted-foreground text-[11px] font-medium">Email</div>
                  <div className="mt-0.5 break-all font-medium text-sm">
                    {c.emailAddress ? (
                      <a href={`mailto:${c.emailAddress}`} className="text-primary hover:underline">
                        {c.emailAddress}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
